import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { SensingType } from '@components/device-info/device-detail/device-detail-summary/device-detail-summary.component';
import { ShipmentStatusViewModel } from '@components/shipment-info/shipment-info-list-table/shipment-info-list-table.component';
import {
  ShipmentGeofenceViewModel,
  ShipmentLocationViewModel,
  ShipmentRouteViewModel,
} from '@components/shipment-route-list/shipment-route-map/shipment-route-map.component';
import { LocationDto } from '@entities/deviceInfo';
import * as turf from '@turf/turf';
import {
  arrivalLocationText,
  createCustomMarker,
  endLocationText,
  startLocationText,
} from '@utils/marker-util';
import {
  createDeviceMarkerElement,
  DEVICE_MARKER_HEIGHT,
} from '@utils/device-marker.util';
import { format } from 'date-fns';
import {
  LngLatBounds,
  Map,
  Marker,
  MarkerOptions,
  NavigationControl,
  Popup,
} from 'maplibre-gl';
import { createMap } from 'maplibre-gl-js-amplify';
import numeral from 'numeral';
import { SensorAlertDetailModel } from '../shipment-info-alert-map-dialog.component';

/** センサの計測結果のViewModel */
export type SensingViewModel = {
  status?: string;
  value?: number;
  eventAt: string;
  eventType?: string;
};

/** 位置情報のViewModel */
export type LocationViewModel = {
  longitude: number;
  latitude: number;
  eventAt: string;
  address?: string;
};

/** 複数センサの計測結果のViewModel */
export type DeviceInfoViewModel = {
  /** デバイスID */
  deviceId: string;
  /** デバイス名 */
  deviceName: string;

  /** 温度 */
  temperature?: SensingViewModel;
  /** 気圧 */
  pressure?: SensingViewModel;
  /** 湿度 */
  humidity?: SensingViewModel;
  /** 傾斜 */
  tilt?: SensingViewModel;
  /** 照度 */
  light?: SensingViewModel;
  /** 落下検知 */
  fallDetected?: SensingViewModel;
  /** 動作検知 */
  mcdDetected?: SensingViewModel;
  /** 明暗検知 */
  lightExposure?: SensingViewModel;
  /** 衝撃検知 */
  shockDetected?: SensingViewModel;

  /** 位置情報 */
  location?: LocationViewModel;
};

/** センサーのオプション */
export type SensorOption = {
  label: string;
  unit: string;
};

@Component({
  selector: 'app-shipment-alert-map',
  standalone: true,
  imports: [],
  templateUrl: './shipment-alert-map.component.html',
  styleUrl: './shipment-alert-map.component.scss',
})
export class ShipmentAlertMapComponent implements OnChanges, OnInit {
  @HostBinding('class')
  protected readonly hostClass = 'tw-block';

  /** mapのHTML要素のid */
  @Input() mapId: string = 'map';

  /** マップの表示高さ(px) */
  @Input() mapHeightPx: number | null = 350;

  /** マップの表示高さ */
  @Input() mapHeight?: string;

  /** デバイス詳細情報履歴 */
  @Input() deviceInfoList: DeviceInfoViewModel[] | undefined;

  /** ルート */
  @Input() route: ShipmentRouteViewModel | undefined;

  /** 現在地 */
  @Input() currentLocation: LocationDto | undefined;

  /** 出発予定 */
  @Input() startDatetime: string = '';

  /** 到着予定 */
  @Input() arrivalDatetime: string | undefined;

  /** 配送アラート履歴 */
  @Input() statusHistory: ShipmentStatusViewModel[] | undefined;

  /** 選択されたアラート詳細 */
  @Input() selectedSensorAlertDetail: SensorAlertDetailModel | undefined;

  /** 選択されたアラートマーカー */
  @Input() selectedMarker: SensorAlertDetailModel | undefined;

  /** アラートではないセンター位置情報も表示する */
  @Input() deviceLocationDisplayed = true;

  /** 選択マーカーを変更した際に通知 */
  @Output() markerChanged = new EventEmitter<SensorAlertDetailModel>();

  private map!: Map;
  private routeMarkers: Marker[] = [];
  private deviceInfoMarkers: Marker[] = [];
  private currentLocationMarker: Marker | undefined;
  private mapReady = false;
  private renderInProgress = false;
  private rerenderRequested = false;

  /** ルートカラー */
  private routeMainColor = '#606060';
  private routeSubColor = '#bcbcbc';

  /** マーカー選択時に表示されているポップアップ */
  private activePopup: Popup | null = null;

  /** ホバー時に表示されているポップアップ */
  private hoverPopup: Popup | null = null;

  private readonly sensorOptions: { [key: string]: SensorOption } = {
    temperature: {
      label: '温度',
      unit: '℃',
    },
    humidity: {
      label: '湿度',
      unit: '%',
    },
    pressure: {
      label: '気圧',
      unit: 'hPa',
    },
    light: {
      label: '照度',
      unit: 'lx',
    },
    tilt: {
      label: '傾斜',
      unit: '°',
    },
  };

  /** 計測種別 */
  private readonly sensingTypeList: Exclude<SensingType, 'battery'>[] = [
    'temperature',
    'humidity',
    'pressure',
    'light',
    'tilt',
    'fallDetected',
    'mcdDetected',
    'lightExposure',
    'shockDetected',
  ];

  private readonly markerColors: { [key: string]: string } = {
    alertmin: '#EF4444',
    alertmax: '#EF4444',
    alert: '#EF4444',
    prealertmin: '#FFD700',
    prealertmax: '#FFD700',
    prealert: '#FFD700',
  };

  /** Alert 系ステータスの判定対象 */
  private readonly alertStatusList = ['alert', 'alertmax', 'alertmin'];
  /** PreAlert 系ステータスの判定対象 */
  private readonly preAlertStatusList = [
    'prealert',
    'prealertmax',
    'prealertmin',
  ];

  async ngOnInit() {
    this.map = await createMap({
      container: this.mapId,
      center: [139.767, 35.681],
      zoom: 11,
      minZoom: 1,
    });
    this.map.addControl(
      new NavigationControl({
        visualizePitch: true,
      }),
    );

    // arrowhead
    const mainArrowHeadImage = await this.createArrowHeadImage(
      40,
      this.routeMainColor,
    );
    this.map.addImage(`arrow-head-${this.routeMainColor}`, mainArrowHeadImage);
    const subArrowHeadImage = await this.createArrowHeadImage(
      40,
      this.routeSubColor,
    );
    this.map.addImage(`arrow-head-${this.routeSubColor}`, subArrowHeadImage);
    await this.waitForMapResize();
    await this.waitUntilMapReady();
    await this.requestRender();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['mapHeightPx'] !== undefined ||
        changes['mapHeight'] !== undefined) &&
      this.map !== undefined
    ) {
      // 高さ変更後に地図をリサイズする（NotReadyの場合に地図の高さを広げるため）
      setTimeout(() => this.map.resize());
    }

    if (!this.mapReady) {
      return;
    }

    if (
      changes['route'] !== undefined ||
      changes['deviceInfoList'] !== undefined ||
      changes['currentLocation'] !== undefined ||
      changes['deviceLocationDisplayed'] !== undefined
    ) {
      this.activePopup?.remove();
      this.requestRender().then();
    }

    if (changes['selectedSensorAlertDetail'] !== undefined) {
      const selectedAlertDetail =
        changes['selectedSensorAlertDetail'].currentValue;
      this.addDeviceInfoMapLayers(
        this.deviceInfoList,
        selectedAlertDetail,
        false,
      ).then();
    }

    if (changes['selectedMarker'] !== undefined) {
      const selectedMarker = changes['selectedMarker'].currentValue;
      this.addDeviceInfoMapLayers(
        this.deviceInfoList,
        selectedMarker,
        false,
      ).then();
    }
  }

  private async waitUntilMapReady(): Promise<void> {
    if (this.map.loaded()) {
      this.mapReady = true;
      return;
    }

    await new Promise<void>((resolve) => {
      this.map.once('load', () => {
        resolve();
      });
    });
    this.mapReady = true;
  }

  private async requestRender(): Promise<void> {
    if (!this.mapReady) {
      return;
    }

    if (this.renderInProgress) {
      this.rerenderRequested = true;
      return;
    }

    this.renderInProgress = true;
    try {
      do {
        this.rerenderRequested = false;
        await this.renderInitialLayers();
      } while (this.rerenderRequested);
    } finally {
      this.renderInProgress = false;
    }
  }

  private async waitMapLoaded(): Promise<boolean> {
    if (this.map === undefined || !this.mapReady) {
      return false;
    }

    return true;
  }

  private async setMainLayers(
    route: ShipmentRouteViewModel | undefined,
    pan = true,
  ) {
    // マップが表示するまで待機
    if (!(await this.waitMapLoaded())) {
      return;
    }

    // マーカー表示
    this.setRouteMarkers(route);

    // ルート表示
    const routeColor = this.deviceLocationDisplayed
      ? this.routeSubColor
      : this.routeMainColor;
    this.drawRoutes(this.routeMarkers, 'shipment', routeColor);

    // PANする
    if (pan) {
      this.panToMarkers(this.routeMarkers);
    }
  }

  private async addDeviceInfoMapLayers(
    markerInfo: DeviceInfoViewModel[] | undefined,
    selectedAlertDetail?: SensorAlertDetailModel,
    pan = true,
  ) {
    // マップが表示するまで待機
    if (!(await this.waitMapLoaded())) {
      return;
    }

    // マーカー表示
    this.setDeviceInfoMarkers(markerInfo, selectedAlertDetail);

    if (this.deviceLocationDisplayed) {
      // ルート表示
      this.drawRoutes(this.deviceInfoMarkers, 'device');
    }

    // PANする
    if (pan) {
      this.panToMarkers(this.routeMarkers);
    }
  }

  private async addCurrentMapLayers(
    markerInfo: LocationDto | undefined,
    pan = true,
  ) {
    // マップが表示するまで待機
    if (!(await this.waitMapLoaded())) {
      return;
    }

    // マーカー表示
    console.debug('[ShipmentAlertMapComponent] currentLocation input', {
      hasCurrentLocation: markerInfo != null,
      latitude: markerInfo?.latitude,
      longitude: markerInfo?.longitude,
      eventAt: markerInfo?.eventAt,
      pan,
    });
    this.setCurrentMarkers(markerInfo);

    // PANする
    if (pan) {
      this.panToMarkers(this.routeMarkers);
    }
  }

  /**
   * ルート情報からマーカーを設定する
   * @param selectedAlertDetail 選択されたアラートの詳細情報
   */
  private setRouteMarkers(route: ShipmentRouteViewModel | undefined) {
    // 初期化
    this.routeMarkers.forEach((marker) => marker.remove());
    this.routeMarkers = [];
    // `main` は地点種別(start/end/via)ではなく、
    // このコンポーネントが作るジオフェンス layer/source を識別するIDプレフィックス。
    this.initGeofence('main');

    if (!route) {
      return;
    }

    // 出発地追加
    const startLocation = route.startLocation;
    if (startLocation) {
      // カスタムマーカーの要素を作成
      const markerElement = createCustomMarker(startLocationText);

      // ジオフェンス表示
      this.createGeofence(startLocation, 'start');

      const longitude = startLocation.longitude;
      const latitude = startLocation.latitude;
      const marker = new Marker({ element: markerElement })
        .setLngLat({ lat: latitude, lng: longitude })
        .addTo(this.map);
      this.routeMarkers.push(marker);

      this.setRouteMarkerPopup(marker, startLocation, this.startDatetime, true);
    }

    // 経由地追加
    const viaLocationList = route.viaLocationList ?? [];
    for (const [index, viaLocation] of viaLocationList.entries()) {
      // カスタムマーカーの要素を作成
      const viaNumber = index + 1;
      const displayText = `${arrivalLocationText}${viaNumber}`;
      const markerElement = createCustomMarker(displayText);

      // ジオフェンス表示
      this.createGeofence(viaLocation, `via${viaNumber}`);

      const longitude = viaLocation.longitude;
      const latitude = viaLocation.latitude;
      const marker = new Marker({ element: markerElement })
        .setLngLat({ lat: latitude, lng: longitude })
        .addTo(this.map);
      this.routeMarkers.push(marker);

      this.setRouteMarkerPopup(marker, viaLocation, undefined);
    }

    // 到着地追加
    const arrivalLocation = route.arrivalLocation;
    if (arrivalLocation) {
      // カスタムマーカーの要素を作成
      const markerElement = createCustomMarker(endLocationText);

      // ジオフェンス表示
      this.createGeofence(arrivalLocation, 'end');

      const longitude = arrivalLocation.longitude;
      const latitude = arrivalLocation.latitude;
      const marker = new Marker({ element: markerElement })
        .setLngLat({ lat: latitude, lng: longitude })
        .addTo(this.map);
      this.routeMarkers.push(marker);

      const arrivalDatetime = this.arrivalDatetime;
      this.setRouteMarkerPopup(marker, arrivalLocation, arrivalDatetime);
    }
  }

  /**
   * アラートリストからマーカーを設定する
   * @param selectedAlertDetail 選択されたアラートの詳細情報
   */
  private setDeviceInfoMarkers(
    deiceInfoList: DeviceInfoViewModel[] | undefined,
    selectedAlertDetail?: SensorAlertDetailModel,
  ) {
    // 初期化
    this.deviceInfoMarkers.forEach((marker) => marker.remove());
    this.deviceInfoMarkers = [];

    if (!deiceInfoList || deiceInfoList.length < 1) {
      return;
    }

    // 現在は配送に対してデバイスは1つのため、最初のデータからデバイス情報を取得する
    const deviceName = deiceInfoList[0].deviceName || '';
    const deviceId = deiceInfoList[0].deviceId || '';

    const alertDetailList = this.convertAlertDetail(deiceInfoList);
    if (!alertDetailList) {
      return;
    }
    let selectedMarkerForPopup = selectedAlertDetail;
    if (selectedAlertDetail) {
      const index = alertDetailList.findIndex((value) =>
        this.equalsAlertDetail(value, selectedAlertDetail),
      );
      if (index >= 0) {
        const [item] = alertDetailList.splice(index, 1);
        alertDetailList.push(item);
        selectedMarkerForPopup = item;
      }
    }

    // マーカー追加
    for (const sensingValue of alertDetailList) {
      const longitude = sensingValue.location?.longitude;
      const latitude = sensingValue.location?.latitude;
      // 位置情報がない場合（null or undefined）の場合はマーカーを追加しない
      if (longitude == null || latitude == null) {
        continue;
      }
      // AlertMax/AlertMin など派生ステータスを色判定のキーへ正規化するため、
      // マーカー色判定用の共通ステータスへ変換する。
      const sensorStatus = this.normalizeMarkerStatus(sensingValue.status);
      const markerColor = this.markerColors[sensorStatus] || '#4169E1';

      // 選択済みアラートがある場合はマーカーを大きくする
      const markerOptions: MarkerOptions =
        selectedAlertDetail &&
        this.equalsAlertDetail(sensingValue, selectedAlertDetail)
          ? { color: markerColor, scale: 1.5 }
          : { color: markerColor };

      const marker = new Marker(markerOptions)
        .setLngLat([longitude, latitude])
        .addTo(this.map);

      // マーカークリック時の挙動設定
      marker
        .getElement()
        .addEventListener('click', () =>
          this.handleMarkerClick(
            deviceName || deviceId,
            sensingValue,
            latitude,
            longitude,
          ),
        );

      // マーカーホバー時の挙動設定
      marker
        .getElement()
        .addEventListener('mouseenter', () =>
          this.handleMarkerMouseEnter(
            deviceName || deviceId,
            marker,
            sensingValue,
            latitude,
            longitude,
          ),
        );

      this.deviceInfoMarkers.push(marker);
    }

    if (selectedMarkerForPopup) {
      // 一覧選択時に対応する地図上位置を即時に確認できるようにするため、
      // 選択されたアラート座標にポップアップを表示する。
      const longitude = selectedMarkerForPopup.location?.longitude;
      const latitude = selectedMarkerForPopup.location?.latitude;

      if (longitude !== undefined && latitude !== undefined) {
        this.showPopupForAlert(
          deviceName || deviceId,
          selectedMarkerForPopup,
          longitude,
          latitude,
        );
      }
    } else {
      this.activePopup?.remove();
    }
  }

  private async renderInitialLayers(): Promise<void> {
    if (!(await this.waitMapLoaded())) {
      return;
    }

    const selectedAlert = this.selectedMarker ?? this.selectedSensorAlertDetail;

    await this.setMainLayers(this.route);

    if (this.deviceLocationDisplayed) {
      this.setCurrentMarkers(undefined);
      await this.addDeviceInfoMapLayers(
        this.deviceInfoList,
        selectedAlert,
        false,
      );
      return;
    }

    await this.addDeviceInfoMapLayers(undefined, undefined, false);
    await this.addCurrentMapLayers(this.currentLocation, false);
    const markers = [...this.routeMarkers];
    if (this.currentLocationMarker) {
      markers.push(this.currentLocationMarker);
    }
    this.panToMarkers(markers);
  }

  private async waitForMapResize(): Promise<void> {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        this.map.resize();
        resolve();
      });
    });
  }

  /**
   * デバイス詳細からマップに表示するアラートデータの形式に変換する
   * @param deviceInfoList デバイス詳細リスト
   * @returns アラートデータ
   */
  private convertAlertDetail(
    deviceInfoList: DeviceInfoViewModel[],
  ): SensorAlertDetailModel[] {
    const alertDetailList: SensorAlertDetailModel[] = [];

    for (const deviceInfo of deviceInfoList) {
      const location = deviceInfo.location;
      // what: 位置情報なしイベントはスキップする。
      if (!location) {
        continue;
      }

      const locationAlertDetailList: SensorAlertDetailModel[] = [];
      for (const key of this.sensingTypeList) {
        const sensingData = deviceInfo[key];
        if (!sensingData || !sensingData.status) continue;
        const option = this.sensorOptions[key];
        locationAlertDetailList.push({
          sensor: key,
          status: sensingData.status,
          unit: option?.unit,
          value: sensingData.value,
          location: location,
          eventAt: sensingData.eventAt,
          eventType: sensingData.eventType,
        });
      }

      // 同一位置の複数センサー状態をすべて描画するとピンが重なり可読性が落ちるため、
      // 位置ごとに代表1件のみを抽出して表示する。
      const representativeAlert = this.selectRepresentativeAlert(
        locationAlertDetailList,
      );
      if (representativeAlert) {
        alertDetailList.push(representativeAlert);
      } else if (this.deviceLocationDisplayed) {
        // アラートのない位置情報も出発後は全て表示する
        alertDetailList.push({
          sensor: '',
          status: '',
          unit: '',
          location: location,
          eventAt: location.eventAt,
        });
      }
    }

    // 時系列にソートする
    alertDetailList.sort((a, b) => {
      const aEventAt = this.toTimestamp(a.location.eventAt);
      const bEventAt = this.toTimestamp(b.location.eventAt);
      return aEventAt - bEventAt;
    });

    return alertDetailList;
  }

  /**
   * 同一位置に紐づく複数アラートから、マーカー表示用の代表アラートを選ぶ。
   *
   * @param alertDetailList 同一位置のアラート候補
   * @returns 優先度と時刻で選択した代表アラート
   */
  private selectRepresentativeAlert(
    alertDetailList: SensorAlertDetailModel[],
  ): SensorAlertDetailModel | undefined {
    if (alertDetailList.length < 1) {
      return undefined;
    }

    return [...alertDetailList].sort((a, b) => {
      // Alert を PreAlert より優先して表示し、注意度を視覚的に統一するため、
      // ステータス優先度で一次ソートする。
      const aPriority = this.getStatusPriority(a.status);
      const bPriority = this.getStatusPriority(b.status);
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // 同じ優先度の場合は、より新しいイベント時刻を優先する
      return this.toTimestamp(b.eventAt) - this.toTimestamp(a.eventAt);
    })[0];
  }

  /**
   * ステータス文字列を描画優先度へ変換する。
   *
   * @param status センサーステータス
   * @returns 0:Alert系, 1:PreAlert系, 2:その他
   */
  private getStatusPriority(status?: string): number {
    const lowerStatus = status?.toLowerCase() || '';
    if (this.alertStatusList.includes(lowerStatus)) {
      return 0;
    }
    if (this.preAlertStatusList.includes(lowerStatus)) {
      return 1;
    }
    return 2;
  }

  /**
   * ステータスをマーカー色判定用の代表値へ正規化する。
   *
   * @param status センサーステータス
   * @returns `alert` / `prealert` / `''`
   */
  private normalizeMarkerStatus(status?: string): string {
    const lowerStatus = status?.toLowerCase() || '';
    if (this.alertStatusList.includes(lowerStatus)) {
      return 'alert';
    }
    if (this.preAlertStatusList.includes(lowerStatus)) {
      return 'prealert';
    }
    return '';
  }

  /**
   * 日時文字列をタイムスタンプに変換する。
   *
   * @param eventAt 日時文字列
   * @returns タイムスタンプ。入力なしの場合は `NaN`
   */
  private toTimestamp(eventAt?: string): number {
    if (!eventAt) {
      return Number.NaN;
    }
    return new Date(eventAt).getTime();
  }

  private equalsAlertDetail(
    target: SensorAlertDetailModel,
    source: SensorAlertDetailModel,
  ): boolean {
    let sameEventAt = target.eventAt == source.eventAt;
    if (target.eventAt != null && source.eventAt != null) {
      // フォーマットを揃えて比較する
      sameEventAt =
        format(target.eventAt, 'TT') === format(source.eventAt, 'TT');
    }
    return (
      target.location.latitude === source.location.latitude &&
      target.location.longitude === source.location.longitude &&
      target.sensor === source.sensor &&
      sameEventAt
    );
  }

  /**
   * イベント時刻が出発時刻以降かを判定する。
   *
   * @param eventAt 判定対象のイベント時刻
   * @returns 出発以降、または判定不可時は `true`
   */
  private isAfterDeparture(eventAt?: string): boolean {
    const departureTimestamp = this.getDepartureTimestamp();
    if (!departureTimestamp || !eventAt) {
      return true;
    }

    const eventTimestamp = new Date(eventAt).getTime();
    if (Number.isNaN(eventTimestamp)) {
      return true;
    }

    return eventTimestamp >= departureTimestamp;
  }

  /**
   * 出発判定に使う基準時刻を取得する。
   *
   * @returns `InProgress` の最初の時刻。なければ `startDatetime`。どちらもなければ `undefined`
   */
  private getDepartureTimestamp(): number | undefined {
    // 実績がある場合は予定時刻より実績時刻を優先したいため
    // ステータス履歴の InProgress の最小時刻を採用する。
    const inProgressTimestampList =
      this.statusHistory
        ?.filter((history) => history.status === 'InProgress')
        .map((history) => new Date(history.eventAt).getTime())
        .filter((timestamp) => !Number.isNaN(timestamp)) ?? [];

    if (inProgressTimestampList.length > 0) {
      return Math.min(...inProgressTimestampList);
    }

    // 実績が取れない場合でも、予定出発時刻を基準に描画範囲を制限したいため、
    // startDatetime をフォールバックの基準時刻として返す。
    const startTimestamp = this.startDatetime
      ? new Date(this.startDatetime).getTime()
      : Number.NaN;
    if (!Number.isNaN(startTimestamp)) {
      return startTimestamp;
    }

    return undefined;
  }

  /**
   * 現在地情報からマーカーを設定する
   * @param markerInfo 現在地情報
   */
  private setCurrentMarkers(markerInfo: LocationDto | undefined) {
    this.currentLocationMarker?.remove();
    this.currentLocationMarker = undefined;

    if (!markerInfo) {
      return;
    }

    const markerElement = createDeviceMarkerElement();

    const currentLocation = markerInfo as LocationDto;
    const longitude = currentLocation.longitude;
    const latitude = currentLocation.latitude;
    const marker = new Marker({ element: markerElement, anchor: 'bottom' })
      .setLngLat({ lat: latitude, lng: longitude })
      .addTo(this.map);
    this.currentLocationMarker = marker;

    this.setCurrentMarkerPopup(marker, currentLocation);
  }

  /**
   * マーカークリック時のポップアップ表示状態を制御する
   * @param sensingValue センサの計測結果
   * @param longitude 経度
   * @param latitude 緯度
   */
  private handleMarkerClick(
    deviceName: string,
    sensingValue: SensorAlertDetailModel,
    latitude: number,
    longitude: number,
  ): void {
    if (
      this.selectedMarker &&
      this.selectedMarker.location?.latitude === latitude &&
      this.selectedMarker.location?.longitude === longitude
    ) {
      // 選択状態のマーカーがクリックされた場合
      this.markerChanged.emit(undefined); // 選択状態を解除
      this.activePopup?.remove(); // ポップアップを削除
      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    } else {
      // 非選択状態のマーカーがクリックされた場合
      // 一度選択状態をすべて解除する
      if (this.activePopup) {
        this.activePopup.remove();
      }
      if (this.selectedSensorAlertDetail) {
        this.markerChanged.emit(undefined);
        this.activePopup?.remove();
      }

      // 新しいアラートを選択状態にする
      this.markerChanged.emit(sensingValue);

      this.showPopupForAlert(deviceName, sensingValue, longitude, latitude);

      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    }
  }

  /**
   * マーカーホバー時のポップアップ表示状態を制御する
   * @param marker マーカー
   * @param sensingValue センサの計測結果
   * @param longitude 経度
   * @param latitude 緯度
   */
  private handleMarkerMouseEnter(
    deviceName: string,
    marker: Marker,
    sensingValue: SensorAlertDetailModel,
    latitude: number,
    longitude: number,
  ): void {
    marker.getElement().style.cursor = 'pointer';

    if (
      !this.selectedMarker ||
      (this.selectedMarker &&
        this.selectedMarker.location?.latitude !== latitude &&
        this.selectedMarker.location?.longitude !== longitude)
    ) {
      const description = this.createPointDescription(
        deviceName,
        sensingValue.value ?? null,
        sensingValue.sensor,
        sensingValue.eventAt,
        sensingValue.location?.address,
      );
      this.hoverPopup = new Popup({
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat({ lon: longitude, lat: latitude })
        .setHTML(description)
        .setOffset([0, -20])
        .addTo(this.map);

      marker.getElement().addEventListener('mouseleave', () => {
        marker.getElement().style.cursor = '';
        if (this.hoverPopup) {
          this.hoverPopup.remove();
        }
      });
    }
  }

  /**
   * 指定されたアラートの内容のポップアップを指定の座標に表示する
   * @param sensingValue センサの計測結果
   * @param longitude 経度
   * @param latitude 緯度
   */
  private showPopupForAlert(
    deviceName: string,
    sensingValue: SensorAlertDetailModel,
    longitude: number,
    latitude: number,
  ): void {
    if (this.activePopup) {
      this.activePopup.remove();
    }

    const description = this.createPointDescription(
      deviceName,
      sensingValue.value ?? null,
      sensingValue.sensor,
      sensingValue.eventAt,
      sensingValue.location?.address,
    );
    this.activePopup = new Popup({
      closeButton: false,
      closeOnClick: false,
    })
      .setLngLat({ lon: longitude, lat: latitude })
      .setHTML(description)
      .setOffset([0, -20])
      .addTo(this.map);
  }

  /** 出発・経由・到着地点マーカーのポップアップを設定する */
  private setRouteMarkerPopup(
    marker: Marker | undefined,
    location: ShipmentLocationViewModel,
    planDateTime: string | undefined,
    start: boolean = false,
  ) {
    // マーカーホバー時に表示されるポップアップの挙動設定
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
    });

    // マーカーホバー時の挙動設定
    marker?.getElement().addEventListener('mouseenter', () => {
      marker.getElement().style.cursor = 'pointer';
      const locationName = location.locationName || '';
      let dateTime: string | undefined;

      if (start) {
        dateTime = this.statusHistory?.find((value) => {
          return (
            value.locationId === location.locationId &&
            value.status === 'InProgress'
          );
        })?.eventAt;
      } else {
        dateTime = this.statusHistory?.find((value) => {
          return (
            value.locationId === location.locationId &&
            (value.status === 'AtStop' || value.status === 'AtDestination')
          );
        })?.eventAt;
      }

      const description = this.createRouteDescription(
        locationName,
        planDateTime,
        dateTime,
        start,
        location.address,
        location.geofence,
      );

      const longitude = location.longitude;
      const latitude = location.latitude;
      if (longitude && latitude) {
        popup
          .setLngLat([longitude, latitude])
          .setHTML(description)
          .setOffset([0, -10])
          .addTo(this.map);
      }
    });
    marker?.getElement().addEventListener('mouseleave', () => {
      marker.getElement().style.cursor = '';
      popup.remove();
    });
  }

  /** 現在地点マーカーのポップアップを設定する */
  private setCurrentMarkerPopup(
    marker: Marker | undefined,
    location: LocationDto,
  ) {
    // マーカーホバー時に表示されるポップアップの挙動設定
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
    });

    // マーカーホバー時の挙動設定
    marker?.getElement().addEventListener('mouseenter', () => {
      marker.getElement().style.cursor = 'pointer';

      const eventAt = location.eventAt;
      const description = this.createCurrentDescription(
        eventAt,
        location.address,
      );

      const longitude = location.longitude;
      const latitude = location.latitude;
      if (longitude && latitude) {
        popup
          .setLngLat([longitude, latitude])
          .setHTML(description)
          .setOffset([0, -DEVICE_MARKER_HEIGHT])
          .addTo(this.map);
      }
    });
    marker?.getElement().addEventListener('mouseleave', () => {
      marker.getElement().style.cursor = '';
      popup.remove();
    });
  }

  /**
   * アラートマーカーのツールチップポップアップのテキストを生成する
   * @param deviceName
   * @param sensingType
   * @param value
   * @param eventAt
   * @param address
   * @returns
   */
  private createPointDescription(
    deviceName: string | undefined,
    value: number | null,
    sensor: string,
    eventAt: string | null = null,
    address?: string,
  ): string {
    const sensorOption = this.sensorOptions[sensor];
    let sensorDescription = '';
    if (sensorOption) {
      const label = sensorOption.label;
      const unit = sensorOption.unit;
      if (value == null) {
        sensorDescription = `${label}：-`;
      } else {
        const formattedValue = numeral(value).format('0.0');
        sensorDescription = `${label}：${formattedValue}&nbsp;${unit}`;
      }
    }

    const eventAtTimestamp =
      eventAt == null ? '-' : format(new Date(eventAt), 'yyyy/MM/dd HH:mm');

    const description = `
        <b>${deviceName}</b>
        <div>${eventAtTimestamp}</div>
        <div>${sensorDescription}</div>
        ${this.createAddressDescription(address)}
    `;

    return description;
  }

  /**
   * 出発・経由・到着地点マーカーのツールチップポップアップのテキストを生成する
   * @param locationName 地名
   * @param address 住所
   * @returns
   */
  private createRouteDescription(
    locationName: string,
    planDatetime: string | undefined,
    dateTime: string | undefined,
    start: boolean = false,
    address?: string,
    geofence?: ShipmentGeofenceViewModel,
  ): string {
    let geofenceText: string = '';
    if (geofence?.geofenceType === 'circle') {
      geofenceText = `<div>ジオフェンス：円  半径${geofence.circleRadius}${geofence.circleRadiusUnit}</div>`;
    } else if (geofence?.geofenceType === 'polygon') {
      geofenceText = `<div>ジオフェンス：フリー描画  ${geofence.polygonList.length - 1} 頂点</div>`;
    }

    if (start) {
      const planDateTimeDescription = planDatetime
        ? `<div>出発予定：${format(new Date(planDatetime), 'yyyy/MM/dd HH:mm')}</div>`
        : '';
      const actualDateTimeDescription = dateTime
        ? `<div>出発日時：${format(new Date(dateTime), 'yyyy/MM/dd HH:mm')}</div>`
        : '';

      const description = `
        <div class="tw-font-bold">${locationName}</div>
        ${planDateTimeDescription}
        ${actualDateTimeDescription}
        ${this.createAddressDescription(address)}
        ${geofenceText}
    `;
      return description;
    } else {
      const actualDateTimeDescription = dateTime
        ? `<div>到着日時：${format(new Date(dateTime), 'yyyy/MM/dd HH:mm')}</div>`
        : '';

      const description = `
        <div class="tw-font-bold">${locationName}</div>
        ${actualDateTimeDescription}
        ${this.createAddressDescription(address)}
        ${geofenceText}
    `;
      return description;
    }
  }

  /**
   * 現在地点マーカーのツールチップポップアップのテキストを生成する
   * @param eventAt 日時
   * @returns
   */
  private createCurrentDescription(eventAt: string, address?: string): string {
    const eventAtTimestamp =
      eventAt == null ? '-' : format(new Date(eventAt), 'yyyy/MM/dd HH:mm');

    const description = `
        <div class="tw-font-bold">デバイス現在位置</div>
        <div>最終接続日時：${eventAtTimestamp}</div>
        ${this.createAddressDescription(address)}
    `;

    return description;
  }

  private createAddressDescription(address?: string): string {
    return `
      <div class="tw-flex tw-items-start">
        <span class="tw-shrink-0">住所：</span>
        <span class="tw-min-w-0 tw-break-words">${address ?? ''}</span>
      </div>
    `;
  }

  /** 位置情報からジオフェンスを表示する */
  private createGeofence(
    location: ShipmentLocationViewModel,
    locationKey: string,
  ): void {
    // `main` はジオフェンスIDの名前空間。
    // `locationKey`(start/end/viaN)と組み合わせてID重複を防ぐ。
    const prefix = 'main';
    const fillColor = '#558ED5';
    const fillOpacity = 0.33;
    const paint = {
      'fill-color': fillColor,
      'fill-opacity': fillOpacity,
    };

    const latitude = location.latitude;
    const longitude = location.longitude;

    const geofence = location.geofence;
    if (!geofence) {
      return;
    }

    const geofenceType = geofence.geofenceType;
    const locationId = location.locationId ?? 'new';

    if (geofenceType === 'circle') {
      // Amazon Location Serviceにはメートル単位で指定するため、メートル単位で表示する
      const radius = geofence.circleRadiusInMeters;
      const options: { steps: number; units: turf.Units } = {
        steps: 64,
        units: 'meters',
      };
      const radiusCenter = [longitude, latitude];
      const circle = turf.circle(radiusCenter, radius, options);

      const sourceId = `${prefix}-circle-${locationKey}-${locationId}-${latitude}-${longitude}`;
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: circle,
      });

      const layerId = `${prefix}-circle-layer-${locationKey}-${locationId}-${latitude}-${longitude}`;
      this.map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        layout: {},
        paint: paint,
      });
    } else if (geofenceType === 'polygon') {
      // 位置情報が4つ以上(最初の要素と最後の要素が一致することと3角形)でないと使用できないため早期リターン
      if (geofence.polygonList.length < 4) {
        return;
      }

      const sourceId = `${prefix}-polygon-${locationKey}-${locationId}-${latitude}-${longitude}`;
      const coordinates = geofence.polygonList.map((polygon) => {
        return [polygon.longitude, polygon.latitude];
      });
      const polygon = turf.polygon([coordinates]);
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: polygon,
      });

      const layerId = `${prefix}-polygon-layer-${locationKey}-${locationId}-${latitude}-${longitude}`;
      this.map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        layout: {},
        paint: paint,
      });
    }
  }

  private panToMarkers(markers: Marker[]) {
    if (markers.length < 1) {
      this.map.setCenter([139.767, 35.681]);
      this.map.setZoom(11);
      return;
    }

    // マーカーの座標を取得
    const markerCoordinates = markers.map((marker) => marker.getLngLat());

    // マーカーの座標を包括する範囲を計算
    const bounds = new LngLatBounds();
    markerCoordinates.forEach((coord) => bounds.extend(coord));

    // マップをPANしてマーカーが見えるようにする
    this.map.fitBounds(bounds, { padding: 50, maxZoom: 16 });
  }

  /** 既存のジオフェンスを初期化する */
  private initGeofence(prefix: string): void {
    const layers = this.map.getStyle().layers || [];
    layers.forEach((layer) => {
      if (
        layer.id.startsWith(`${prefix}-circle-layer-`) ||
        layer.id.startsWith(`${prefix}-polygon-layer-`)
      ) {
        this.map.removeLayer(layer.id);
      }
    });

    const sources = this.map.getStyle().sources || {};
    Object.keys(sources).forEach((sourceId) => {
      if (
        sourceId.startsWith(`${prefix}-circle-`) ||
        sourceId.startsWith(`${prefix}-polygon-`)
      ) {
        this.map.removeSource(sourceId);
      }
    });
  }

  private createArrowHeadImage(
    size: number,
    color = '#606060',
  ): Promise<ImageBitmap> {
    const data = `
      <svg width='${size}' height='${size}' viewBox='0 0 100 100' 
       xmlns='http://www.w3.org/2000/svg' version='1.1'><polygon fill='${color}' 
       stroke='gray' stroke-width='1' points='20,90 50,10 80,90 50,70' 
       transform='rotate(90 50 50)'/>
      </svg>
    `;
    return new Promise<ImageBitmap>((resolve) => {
      const img = new Image(size, size);
      img.src = 'data:image/svg+xml;base64,' + btoa(data);
      img.onload = () => resolve(createImageBitmap(img));
    });
  }

  private drawRoutes(
    markers: Marker[],
    prefix: string,
    routeColor = '#606060',
  ) {
    // 初期化
    const routeId = `${prefix}-route`;
    const directionId = `${prefix}-direction`;
    if (this.map.getLayer(routeId) !== undefined) {
      this.map.removeLayer(routeId);
    }
    if (this.map.getLayer(directionId) !== undefined) {
      this.map.removeLayer(directionId);
    }
    if (this.map.getSource(routeId) !== undefined) {
      this.map.removeSource(routeId);
    }

    // マーカーの座標を取得
    const markerCoordinates = markers.map((marker) => {
      const lngLat = marker.getLngLat();
      return [lngLat.lng, lngLat.lat];
    });

    const route = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: markerCoordinates,
          },
        },
      ],
    };

    this.map.addSource(routeId, {
      type: 'geojson',
      data: route,
    });

    this.map.addLayer({
      id: routeId,
      type: 'line',
      source: routeId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': routeColor,
        'line-width': 5,
      },
    });

    this.map.addLayer({
      id: directionId,
      type: 'symbol',
      source: routeId,
      layout: {
        'symbol-placement': 'line',
        'icon-image': `arrow-head-${routeColor}`,
        'icon-offset': [15, 0],
        'symbol-spacing': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          100,
          20,
          500,
        ],
      },
    });
  }
}
